import {
  checkCardAuthenticity,
  checkEligibilityApi,
  generateQr,
  getTransactionDetailsById,
} from '@api/card';
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { BoardingPassCapture, CardState, EnterCardDetails } from '@utils/types';
import { toast } from 'react-toastify';

export const checkEligibility = createAsyncThunk(
  'checkEligibility',
  async (cardPayload: EnterCardDetails) => {
    const cardAuthenticRes = await checkCardAuthenticity(
      // cardPayload.cardNumber.slice(0, 6),
      cardPayload.cardNumber,
    );

    if (
      !cardAuthenticRes?.bank_name ||
      (cardAuthenticRes?.bank_name && cardAuthenticRes?.bank_name === 'unknown')
    ) {
      toast.error('Incorrect card details.');
      throw new Error('Incorrect card details');
    }

    const numericPaxCount = Number(cardPayload?.paxCount);
    const validPaxCount =
      typeof numericPaxCount === 'number' &&
      !isNaN(numericPaxCount) &&
      isFinite(numericPaxCount) &&
      numericPaxCount > 0
        ? numericPaxCount
        : 1;

    const body = {
      card: {
        bin: cardPayload.cardNumber,
        cardId: cardAuthenticRes?.cardId ?? '',
        clientId: cardAuthenticRes?.clientId ?? '',
        categoryId: cardAuthenticRes?.categoryId ?? '',
      },
      paxCount: validPaxCount,
    };
    const result = await checkEligibilityApi(body);

    result.data.bankDetails = {
      bankName: cardAuthenticRes?.bank_name ?? '',
      cardType: cardAuthenticRes?.type ?? '',
      network: cardAuthenticRes?.network ?? '',
    };

    return { result, cardPayload, cardAuthenticRes };
  },
);

export const getTransactionDetails = createAsyncThunk(
  'getTransactionDetails',
  async (transactionId: string, { signal }) => {
    const result = await getTransactionDetailsById(transactionId, signal);

    return result;
  },
);

export const getQR = createAsyncThunk(
  'getQR',
  async (transactionId: string, { signal }) => {
    const qrCodeObj = await generateQr(transactionId, signal);
    return qrCodeObj;
  },
);

const initialState: CardState = {
  cardDetails: {},
  cardAuthenticityDetails: {},
  boardingPassMandatory: false,
  boardingPassCapture: null,
  cardInput: {
    fullName: '',
    cardNumber: '',
    expirationMonth: 0,
    expirationYear: 0,
    cvc: '',
  },
  bookingId: '',
  paymentStatus: '',
  rrnumber: '',
  transactionId: '',
  qr: {
    png: '',
  },
  error: '',
  bookingDate: null,
  bookingTime: null,
  loungeName: '',
  loungeCity: '',
  guestPax: 0,
  bookingStatus: '',
  checkIn: false,
  linkedBoardingPass: null,
  lastFourDigit: '',
  issuerNetwork: '',
  cardType: '',
};

export const cardSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    saveCardInputs: (state, action) => {
      state.cardInput = action.payload;
      state.boardingPassCapture = null;
    },
    setBoardingPassCapture: (
      state,
      action: PayloadAction<BoardingPassCapture | null>,
    ) => {
      state.boardingPassCapture = action.payload;
    },
    clearBoardingPassCapture: (state) => {
      state.boardingPassCapture = null;
    },
    setBookingDate: (state, action: PayloadAction<string | null>) => {
      state.bookingDate = action.payload;
    },
    setBookingTime: (state, action: PayloadAction<string | null>) => {
      state.bookingTime = action.payload;
    },
    saveGuestCount: (state, action) => {
      state.guestPax = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkEligibility.pending, (state) => {
      state.boardingPassMandatory = false;
      state.boardingPassCapture = null;
    });
    builder.addCase(checkEligibility.fulfilled, (state, action) => {
      state.cardDetails = action.payload.result.data;
      state.boardingPassMandatory = Boolean(
        action.payload.result.data.boarding_pass_mandatory,
      );
      state.boardingPassCapture = null;
      state.cardAuthenticityDetails = action.payload.cardAuthenticRes;
      state.error = '';
    });
    builder.addCase(checkEligibility.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to check eligibility';
      state.boardingPassMandatory = false;
      state.boardingPassCapture = null;
    });
    builder.addCase(getTransactionDetails.fulfilled, (state, action) => {
      state.bookingId = action.payload.bookingId ?? '';
      state.paymentStatus = action.payload.status ?? '';
      state.cardInput.paxCount =
        action.payload.cardholder_pax + action.payload.guest_pax;
      state.cardDetails.amount = {
        ...state.cardDetails.amount,
        paid: 0,
        complimentary: action.payload.amount,
        total: action.payload.amount,
      };
      state.rrnumber = action.payload.rrnumber;
      // Store date as ISO string to maintain Redux serialization
      state.bookingDate = action.payload.booking_date || null;
      state.loungeName = action.payload.lounge_name;
      state.loungeCity = action.payload.lounge_city;
      state.error = '';
      state.guestPax = action.payload.guest_pax ?? 0;
      state.bookingStatus = action.payload.booking_status;
      state.checkIn = action.payload.check_in;
      state.linkedBoardingPass = action.payload.boarding_pass?.pnr
        ? action.payload.boarding_pass
        : null;
      state.lastFourDigit = action.payload.last_4_digits;
      state.issuerNetwork = action.payload.issuer_network;
      state.cardType = action.payload.card_type;
    });
    builder.addCase(getTransactionDetails.rejected, (state, action) => {
      if (action.meta.aborted) return;
      state.error =
        action.error.message || 'Failed to fetch transaction details';
    });
    builder.addCase(getQR.fulfilled, (state, action) => {
      const qrPayload = action.payload?.data ?? action.payload ?? {};

      state.qr.png =
        qrPayload.png ?? qrPayload.qrString ?? qrPayload.qr_string ?? '';
      state.transactionId =
        qrPayload.transaction_id ?? qrPayload.transactionId ?? action.meta.arg;
      state.error = '';
    });
    builder.addCase(getQR.rejected, (state, action) => {
      if (action.meta.aborted) return;
      state.error = action.error.message || 'Failed to generate QR code';
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  saveCardInputs,
  setBoardingPassCapture,
  clearBoardingPassCapture,
  saveGuestCount,
  setBookingDate,
  setBookingTime,
} = cardSlice.actions;

export default cardSlice.reducer;
